import React, { useCallback, useEffect, useState } from 'react';
import { FlatList, ImageBackground, Pressable, ScrollView, View } from 'react-native';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { useStore } from 'src/contexts/StoreContext';
import { brand, buttons, neutrals } from 'src/theme/colors';
import TextLato from '../../../text/text-lato/TextLato';
import { checkScopes, getAccountInfo, readAccessToken } from 'src/helpers/google.helper';
import { SCOPE_GOOGLE_PHOTO } from 'src/constants/google.constants';
import { SELECTED_MENU_ITEMS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { Icon } from '../../../icon/Icon';
import { ICON_NAMES } from '../../../icon/icon.helper';
import { styleMobile } from './ModalUploadMultisource.style';
import { observer } from 'mobx-react-lite';
import { SourcesEventType, sourcesMobileNew, sourcesMobileOld } from './ModalUploadMultisource.constants';
import { PickerSourceButton } from '../../../buttons/picker-source-button/PickerSourceButton';
import { PrimaryButton } from '../../../buttons/primary-button/PrimaryButton';
import { PrimaryButton as PrimaryButtonNew } from '../../../../editor-v3/components/buttons/primary-button/PrimaryButton';
import { useNavigation } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { DynamicModalProps } from '../DynamicModal.types';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { pickImages } from 'src/store/_shared/imagePickerUtils';
import braze from 'src/_shared/braze';
import { DiscountScale } from '@photosi/expo-photosi-sdk/build/Project';
import { getSourceType } from 'src/helpers/tracking.helper';
import { ModalUploadMultisourceBar } from './_shared/modal-upload-multisource-bar/ModalUploadMultisourceBar';
import { usePickerLogic } from './picker/Picker.logic';
import { STEPS } from 'src/editor-v3/components/upload-picker/UploadSteps';
import OptionSelector, { OptionSelectorOption } from '@photosi/rn-components/option-selector/OptionSelector';
import { LookupChoiceForStep } from '@photosi/expo-photosi-sdk/build/PrintUtils';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { Tile } from 'src/editor-v3/components/tile/Tile';
import { getFormattedPrice } from 'src/helpers/price.helper';
import { Neutrals } from 'src/theme-v2/colors';
import { ChatButton } from 'src/_shared/chat/ChatButton';

export interface ModalUploadMultiSourceMobileProps extends DynamicModalProps {
  discount: DiscountScale[];
  isOnline: boolean;
  selectedCard: string | undefined;
  setSelectedCard: (data: string) => void;
  step: STEPS;
  options: OptionSelectorOption[];
  setSelectedOption: (data: string) => void;
  initialOption: string;
  getCards: LookupChoiceForStep[];
  goBack: () => void;
  onConfirmStep: () => void;
  backgroundImageUrl?: string;
}

export const ModalUploadMultiSourceMobile = observer((props: ModalUploadMultiSourceMobileProps) => {
  const {
    project: { activeProject, setShowUploadPhotosBanner },
    i18n,
    dynamicModal: { closeModal },
    googleAccount: { setAccessToken, setIsUserLogged, setProfilePic, setEmail, setHasGivenAccessToPhoto },
    data: { percentage, isImageShareStarted, setPercentage, setUploadError },
    toastStore,
  } = useStore();
  const {
    dynamicModalVisible,
    dynamicModalLayer: {
      selectedMenuItem,
      setSelectedItem,
      newUploadMultiSourceCallback,
      isUploadingPhoto,
      uploadMultiSourceCallback,
    },
    isOnline,
    selectedCard,
    setSelectedCard,
    step,
    options,
    setSelectedOption,
    initialOption,
    getCards,
    goBack,
    onConfirmStep,
    backgroundImageUrl,
  } = props;

  const thumbnailsPresent = activeProject?.thumbnailImages?.length > 0;
  const newRoute = useNavigation().getState()?.routes[0].name === ROUTE_NAMES.EditorNew;
  const [containerWidth, setContainerWidth] = useState(0);

  const sourcesMobile = newRoute ? sourcesMobileNew : sourcesMobileOld;

  useEffect(() => {
    const accessToken = readAccessToken();
    if (accessToken) {
      setAccessToken(accessToken);
      getAccountInfo({ accessToken: accessToken }).then(res => {
        setProfilePic(res.picture);
        setEmail(res.email);
      });
      checkScopes(accessToken).then(res => {
        const userHasGivenAccessToPhoto = res.scope.includes(SCOPE_GOOGLE_PHOTO);
        if (userHasGivenAccessToPhoto) {
          setHasGivenAccessToPhoto(true);
          amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceAttachSuccess, {
            source_type: SourcesEventType.GooglePhoto,
          });
        } else {
          setHasGivenAccessToPhoto(false);
          amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceAttachError, {
            source_type: SourcesEventType.GooglePhoto,
            error: '',
          });
        }
      });
      setIsUserLogged(true);
    }
  }, []);

  useEffect(() => {
    if (
      dynamicModalVisible &&
      activeProject?.addingProductCount! >= 1 &&
      activeProject?.alreadyAddedProductCount! >= 1 &&
      !activeProject?.isEmpty
    ) {
      closeModal();
    }
  }, [activeProject?.alreadyAddedProductCount, activeProject?.addingProductCount, activeProject?.isEmpty]);

  const handleOnClose = () => {
    if (selectedMenuItem !== SELECTED_MENU_ITEMS.Gallery) {
      setSelectedItem(SELECTED_MENU_ITEMS.Gallery);
    } else if (step === STEPS.Picker && getCards.length > 0) {
      goBack();
    } else {
      setUploadError(false);
      setShowUploadPhotosBanner(false);
      closeModal();
      activeProject?.deselectAllThumbnailImages();
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.closeClick);
    }
  };

  const loadingPercentageBar = isUploadingPhoto || (percentage > 0 && percentage <= 100) || isImageShareStarted;

  useEffect(() => {
    if (percentage === 100) {
      setTimeout(() => {
        setPercentage(0);
      }, 500);
    }
  }, [percentage]);

  useEffect(() => {
    if (selectedMenuItem !== SELECTED_MENU_ITEMS.Gallery && isImageShareStarted) {
      setSelectedItem(SELECTED_MENU_ITEMS.Gallery);
    }
  }, [isImageShareStarted]);

  const onPressHandlerGallery = async () => {
    const productImages: PickedImage[] = await pickImages({
      allowsMultipleSelection: true,
      i18n,
      errorCallback: ({ areAllPhotosInvalid, errorMessage }) => {
        if (areAllPhotosInvalid) {
          closeModal();
        }
        toastStore.showErrorToast({
          id: 'alert-uploading-photos-error-toast',
          message: errorMessage,
        });
      },
      newRoute: newRoute,
    });
    if (!!productImages.length) {
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick, { source_type: SourcesEventType.Gallery });
      braze.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick);
      if (newRoute) {
        productImages.forEach(file => (file.source = SELECTED_MENU_ITEMS.Gallery));
        await activeProject?.addImages(productImages);
      } else {
        uploadMultiSourceCallback({ files: productImages, sourceType: SourcesEventType.Computer });
      }

      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
        n_photo: productImages.length,
        source_type: SourcesEventType.Gallery,
      });
    }
  };

  const { Picker, pickerTitleKey, pickerDescriptionKey } = usePickerLogic(selectedMenuItem);

  const renderItem = useCallback(
    ({ item, index }: { item: LookupChoiceForStep; index: number }) => {
      const CARD_WIDTH = containerWidth * 0.32;
      const isDiscount = item.price != item.fullPrice;
      return {
        code: item.name,
        previewImageUrl: item?.imageUrl,
        title: i18n.t(`modal-picker-my-photos:${item.name}_mobile`),
        index,
        fullPrice: getFormattedPrice({
          price: item.fullPrice || 0,
          withCurrency: true,
        }),
        ...(isDiscount && {
          price: getFormattedPrice({
            price: item.price || 0,
            withCurrency: true,
          }),
        }),
        isSelected: item.name === selectedCard,
        width: CARD_WIDTH,
        borderColor: Neutrals.neutralGreys.GreyNatural150,
        onPress: () => setSelectedCard(item.name),
      };
    },
    [selectedCard, containerWidth],
  );

  return (
    <View
      style={styleMobile.container}
      onLayout={e => {
        setContainerWidth(e.nativeEvent.layout.width - 32);
      }}
    >
      {sourcesMobile.length > 1 && thumbnailsPresent && selectedMenuItem === SELECTED_MENU_ITEMS.Gallery && (
        <View style={styleMobile.sideMenu}>
          <ModalUploadMultisourceBar
            isLoadingPercentageBarVisible={loadingPercentageBar}
            isSpinnerVisible={isImageShareStarted}
            selectedThumbnailsCount={activeProject?.selectedThumbnails.length || 0}
            discountScales={props.discount}
            percentage={percentage}
          />

          <View style={styleMobile.textContainer}>
            <TextLato style={styleMobile.otherChoisesText} numberOfLines={1}>
              {i18n.t('upload:upload-from')}
            </TextLato>
          </View>
          <ScrollView
            contentContainerStyle={[styleMobile.sourcesContainer]}
            style={{ width: '100%', paddingHorizontal: 24 }}
            horizontal
            showsHorizontalScrollIndicator={false}
          >
            {sourcesMobile.map((source, index) => (
              <PickerSourceButton
                style={styleMobile.pickerButton}
                innerStyle={styleMobile.pickerInnerButton}
                pickerStyle={{ color: brand.main.water }}
                key={source}
                source={source}
                onPress={async () => {
                  setSelectedItem(source);
                  const source_type = getSourceType(source);
                  amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceClick, { source_type });
                  if (source === SELECTED_MENU_ITEMS.Gallery) {
                    onPressHandlerGallery();
                  }
                }}
                text={i18n.t(`upload:${source}`)}
              />
            ))}
          </ScrollView>

          {newRoute && (
            <View style={{ paddingHorizontal: 24, width: '100%' }}>
              <PrimaryButton
                text={i18n.t('modal-picker-my-photos:confirm')}
                style={{ paddingHorizontal: 50, width: '100%' }}
                disabled={activeProject?.selectedThumbnails.length === 0 || ![0, 100].includes(percentage)}
                onPress={async () => {
                  const googlePhotoCounter = activeProject?.selectedThumbnails.filter(
                    t => t.source === SELECTED_MENU_ITEMS.GooglePhoto,
                  )?.length;
                  const localPhotoCounter = activeProject?.selectedThumbnails.filter(
                    t => t.source === SELECTED_MENU_ITEMS.Computer,
                  )?.length;
                  const smartphonePhotoCounter = activeProject?.selectedThumbnails.filter(
                    t => t.source === SELECTED_MENU_ITEMS.Smartphone,
                  )?.length;
                  const familyFriendsPhotoCounter = activeProject?.selectedThumbnails.filter(
                    t => t.source === SELECTED_MENU_ITEMS.FamilyFriends,
                  )?.length;
                  amplitude.trackEvent(AnalyticsEvents.dynamicPicker.continueClick, {
                    n_photo: activeProject?.selectedThumbnails.length,
                    n_photos_facebook: 0,
                    n_photos_gphoto: googlePhotoCounter ?? 0,
                    n_photos_local: localPhotoCounter ?? 0,
                    n_photos_smartphone: smartphonePhotoCounter ? smartphonePhotoCounter : 0,
                    n_photos_family_friends: familyFriendsPhotoCounter ? familyFriendsPhotoCounter : 0,
                  });

                  newUploadMultiSourceCallback({
                    fileIds: activeProject?.selectedThumbnails.map(t => t.originalImageId),
                  });
                }}
              >
                {({ disabled }) => (
                  <TextLato style={[styleMobile.counter, disabled && { color: neutrals.neutralGrays.ash }]}>
                    {activeProject?.selectedThumbnails.length.toString()}
                  </TextLato>
                )}
              </PrimaryButton>
            </View>
          )}
        </View>
      )}
      {step === STEPS.Picker ? (
        <>
          <View style={[styleMobile.pickerWrapper, { marginLeft: 0, zIndex: 10, elevation: 10 }]}>
            <Picker {...props} isOnline={isOnline} />
          </View>
          <View style={styleMobile.header}>
            <Pressable style={styleMobile.ctaBack} onPress={handleOnClose}>
              <Icon name={ICON_NAMES.ArrowLeft1} color={brand.main.water} />
            </Pressable>
            <TextLato style={styleMobile.headertext}>{i18n.t(pickerTitleKey)}</TextLato>
            {pickerDescriptionKey && (
              <TextLato style={styleMobile.description}>{i18n.t(pickerDescriptionKey)}</TextLato>
            )}
          </View>
        </>
      ) : (
        <View style={styleMobile.optionsWrapper}>
          <ImageBackground
            source={{ uri: backgroundImageUrl }}
            style={{ flex: 1 }}
            resizeMode="cover"
            imageStyle={styleMobile.imageStyle}
          >
            <View style={styleMobile.header}>
              <Pressable style={styleMobile.ctaBack} onPress={handleOnClose}>
                <Icon name={ICON_NAMES.ArrowLeft1} color={buttons.ghost.buttonGhostIconDefault} />
              </Pressable>
              <TextLato style={styleMobile.headertext}>
                {i18n.t('modal-picker-my-photos:options-header-title-mobile')}
              </TextLato>
              <View style={styleMobile.ctaBot}>
                <ChatButton newGraphic focusGroup={FocusGroups.AFTER_PRODUCT} focusOrder={0} />
              </View>
            </View>
          </ImageBackground>
          <View style={styleMobile.bottomSheet}>
            <View style={styleMobile.containerTop}>
              <TextLato style={styleMobile.headertext}>
                {i18n.t('modal-picker-my-photos:options-title-mobile')}
              </TextLato>
              <View style={{ alignSelf: 'center' }}>
                <OptionSelector options={options} onChange={setSelectedOption} defaultValue={initialOption} />
              </View>
            </View>
            <FlatList
              data={getCards}
              keyExtractor={item => item.productConfigurationId}
              horizontal={true}
              showsHorizontalScrollIndicator={false}
              style={{ flexGrow: 0 }}
              contentContainerStyle={styleMobile.contentContainerStyle}
              renderItem={({ item, index }) => {
                const props = renderItem({ item, index });
                return <Tile {...props} />;
              }}
              extraData={selectedCard}
            />
            <View style={{ flex: 1, alignItems: 'center', paddingHorizontal: 16 }}>
              <PrimaryButtonNew
                text={i18n.t('modal-picker-my-photos:continue')}
                focusGroup={FocusGroups.MODAL}
                focusId={modalTestID(TestIDs.CONFIRM)}
                testID={modalTestID(TestIDs.CONFIRM)}
                focusOrder={(activeProject?.thumbnailImages?.length ?? 0) + 11}
                onPress={onConfirmStep}
                size={BUTTON_SIZES.REGULAR}
                type={BUTTON_TYPES.DEFAULT}
                layout={'fill'}
              />
            </View>
          </View>
        </View>
      )}
    </View>
  );
});
